import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { CustomEvent } from "schemas/functions";

import TemplateEditButton from "components/TemplateEditButton";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

const styles = {
  drawer: {
    "& .MuiDrawer-paper": {
      position: "absolute",
      right: "-8px",
      boxShadow: "-8px 0px 16px rgba(0, 0, 0, 0.1)",
    },
  },
  editIcon: {
    fontSize: 14,
    color: grey[500],
  },
  closeIcon: {
    position: "absolute",
    right: "18px",
    top: "12px",
    fontSize: 18,
    color: grey[400],
  },
  link: {
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      color: "primary.main",
      textDecoration: "underline",
    },
  },
};

interface IProps {
  open: boolean;
  emailSettingId: number;
  handleClose: (e?: CustomEvent) => void;
  templates: Template[] | null;
  setTemplates: Dispatch<SetStateAction<Template[]>>;
}

type TemplateError = {
  idx: number;
  message: string;
};

export default function EditFallbackTemplatesDrawer({
  open,
  emailSettingId,
  handleClose,
  templates,
  setTemplates,
}: IProps) {
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    setCreateTemplateDialogOpen,
    fetchOutreachTemplates,
  } = useContext(OutreachTemplatesContext);
  const { emailSettings } = useContext(OrganizationUserContext);

  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const selectedFollowups = modifiedTemplates?.slice(
    1,
    modifiedTemplates?.length,
  );

  const handleGenerateTemplate = () => {
    trackEvent("Generate Template Button Clicked", {
      Source: "VA Add Niche",
    });
    setCreateTemplateDialogOpen(true);
  };

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...modifiedTemplates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = template;
      setModifiedTemplates(copy);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...modifiedTemplates];
    copy.splice(index, 1);
    setModifiedTemplates(copy);
  };

  const addFollowUp = () => {
    setModifiedTemplates([...modifiedTemplates, followupTemplates?.[0]]);
  };

  const initializeTemplates = () => {
    if (templates && templates.length > 0) {
      setModifiedTemplates(templates);
      return;
    }

    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }

    const presetTemplates = emailSettings?.emailSettingTemplates || [];
    const matchedTemplates = [];

    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) {
        matchedTemplates.push(template);
      }
    }
    if (matchedTemplates?.length > 0) {
      setModifiedTemplates(matchedTemplates);
      return;
    }

    if (filteredOutreachTemplates?.length > 0) {
      setModifiedTemplates([filteredOutreachTemplates[0]]);
    } else if (filteredOutreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    }
  };

  const saveFallbackTemplates = async () => {
    setLoading(true);

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-settings/${emailSettingId}`,
        "PUT",
        {},
        {
          templateIds: modifiedTemplates
            .map((t) => t.id)
            .filter((id): id is number => id !== undefined),
        },
        true,
      );

      const jsonMessage = await res.json();

      if (!res.ok) {
        if (jsonMessage.field) {
          if (
            jsonMessage.field === "templates" &&
            jsonMessage.templateIdx !== undefined &&
            jsonMessage.templateIdx !== null
          ) {
            setTemplatesError({
              idx: jsonMessage.templateIdx,
              message: jsonMessage.message,
            });
            return;
          }
        }
        setAlert(jsonMessage.message, "error");
      } else {
        setTemplates(modifiedTemplates);
        onClose();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates?.length, open]);

  const onClose = () => {
    setModifiedTemplates([]);
    handleClose();
  };

  return (
    <Drawer open={open} anchor="right" onClose={onClose} sx={styles.drawer}>
      <Box
        sx={{
          p: 4,
          minWidth: "30vw",
          maxWidth: "500px",
        }}
      >
        <Typography variant="h4" sx={{ mr: 4, mt: 2 }} paragraph>
          Default Templates
        </Typography>
        <Typography color="textSecondary">
          These templates are used in cases when a brand is pitched that doesn't
          fit any of your defined niches.
        </Typography>
        <IconButton sx={styles.closeIcon} onClick={onClose}>
          <Box component="span" className="fa-regular fa-xmark-large" />
        </IconButton>
        <Divider sx={{ mt: 2, mb: 4 }} />
        <Box>
          <Typography variant="h6" paragraph>
            Let's set up your outreach template 🚀
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          {modifiedTemplates[0] ? (
            <OutreachSelect
              templates={modifiedTemplates}
              handleChange={handleTemplateChange}
              filteredOutreachTemplates={filteredOutreachTemplates}
              templateFromEmailSettings={templateFromEmailSettings}
              addNewOption={handleGenerateTemplate}
            />
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleGenerateTemplate}
              sx={{ height: 25, mt: 0.4, textTransform: "none" }}
              disableElevation
            >
              Generate Template
            </Button>
          )}
          {templatesError && templatesError.idx === 0 && (
            <Grid container>
              <Typography
                variant="caption"
                sx={{ fontStyle: "italic", mt: 0.5, color: "error.main" }}
              >
                {templatesError.message}
              </Typography>
            </Grid>
          )}
        </Box>
        <Divider sx={{ my: 4 }} />
        <Box>
          <Typography variant="h6" paragraph>
            Let's set up your follow-ups
          </Typography>
        </Box>
        <Grid container sx={{ mb: 2 }} columnSpacing={{ xs: 0, sm: 0.5 }}>
          <Grid item xs={12}>
            {selectedFollowups?.map((followup, index) => (
              <Grid container sx={{ mb: 1 }} key={index}>
                <Grid container alignItems="center">
                  <Select
                    onChange={(e) => {
                      e.stopPropagation();
                      handleTemplateChange(e, index + 1);
                    }}
                    onMouseUp={(e) => {
                      e.stopPropagation();
                    }}
                    value={followup?.id}
                    size="small"
                  >
                    {filteredFollowUps?.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        <Box>
                          {template.name}
                          {template.id &&
                            templateFromEmailSettings?.includes(
                              template.id,
                            ) && (
                              <Chip
                                label="Last Used"
                                size="small"
                                sx={{
                                  ml: 1,
                                  fontSize: 10,
                                  backgroundColor: "primary.light",
                                }}
                              />
                            )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>

                  {followup?.id && (
                    <TemplateEditButton
                      sx={{ ml: 1 }}
                      templateId={followup.id}
                    />
                  )}

                  <IconButton
                    sx={{ fontSize: 13, color: grey[500] }}
                    onClick={() => removeFollowUp(index + 1)}
                  >
                    <Box component="i" className="fa-regular fa-trash" />
                  </IconButton>
                </Grid>
                {templatesError && templatesError.idx === index + 1 && (
                  <Grid container>
                    <Typography
                      variant="caption"
                      sx={{ fontStyle: "italic", mt: 0.5, color: "error.main" }}
                    >
                      {templatesError.message}
                    </Typography>
                  </Grid>
                )}
                <Grid container>
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: "italic", fontSize: 10 }}
                    color="textSecondary"
                  >
                    Sent 4 business days after previous email
                  </Typography>
                </Grid>
              </Grid>
            ))}
            {followupTemplates?.length > 0 &&
              modifiedTemplates.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
                <Grid container alignItems="center">
                  <Grid item xs md>
                    <Button
                      disableElevation
                      onClick={addFollowUp}
                      variant="contained"
                    >
                      + Follow Up
                    </Button>
                  </Grid>
                </Grid>
              )}
            {followupTemplates.length === 0 && (
              <Button
                variant="contained"
                size="small"
                onClick={handleGenerateTemplate}
                sx={{
                  height: 25,
                  mt: 0.4,
                  textTransform: "none",
                }}
                disableElevation
              >
                Generate Follow-up
              </Button>
            )}
          </Grid>
        </Grid>
        <Divider sx={{ my: 4 }} />
        <Box>
          <Typography sx={{ mb: 4, maxWidth: "400px" }}>
            To keep campaigns consistent, it takes at most 7 days for your
            changes to take effect.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            sx={{
              px: 4,
            }}
            disableElevation
            onClick={() => {
              saveFallbackTemplates();
            }}
          >
            Save Changes
          </LoadingButton>
        </Box>
      </Box>
    </Drawer>
  );
}
